import { inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { merge } from 'rxjs';
import { AiChatInternalPromptService } from '@panaya/ai-chat/domain';
import { injectAiChatEventNotifier } from '../conversation-tools/ai-chat-events.provider';

/**
 * What the canvas did in the current turn. Each poll returns every command of the turn so far, so a `tool_start`
 * comes back on every later poll; the frame interceptor opens only for the registered `tool_start`s past the count
 * already handled. Resets on every new prompt.
 */
@Injectable({ providedIn: 'root' })
export class CanvasTurnTracker {
    private readonly _handledToolStarts = signal(0);
    private readonly _openedThisTurn = signal(false);

    /** How many registered `tool_start`s of this turn were already handled. */
    public readonly handledToolStarts = this._handledToolStarts.asReadonly();
    /** True when this turn opened the canvas, as opposed to an earlier turn. */
    public readonly openedThisTurn = this._openedThisTurn.asReadonly();

    constructor() {
        merge(injectAiChatEventNotifier().promptRequest, inject(AiChatInternalPromptService).internalPrompt$)
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.startTurn());
    }

    public startTurn(): void {
        this._handledToolStarts.set(0);

        this._openedThisTurn.set(false);
    }

    public markHandled(count: number): void {
        this._handledToolStarts.set(count);
    }

    public markOpened(): void {
        this._openedThisTurn.set(true);
    }
}
